import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Eye, RefreshCw, Store, Target, UserRoundCog, Users, Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AdminPage,
  EmptyState,
  KpiCard,
  PillTabs,
  formatDateTime,
  formatNumber,
} from '../../../components/design-system/admin';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { useAdminOverviewApi } from '../../../common/hooks/use-admin-overview-api.hook';
import type { OverviewDays, OverviewResponse } from '../../../common/models/admin-overview.model';
import { useAdminNav } from '../../../modules/superadmin/shell/admin-nav.store';
import { AttentionList } from '../../../modules/superadmin/overview/AttentionList';
import { ActivityChart } from '../../../modules/superadmin/overview/ActivityChart';
import { VendorLeaderboard } from '../../../modules/superadmin/overview/VendorLeaderboard';
import { HealthCard } from '../../../modules/superadmin/overview/HealthCard';
import { AiSpendCard } from '../../../modules/superadmin/overview/AiSpendCard';
import { DeskCard } from '../../../modules/superadmin/overview/DeskCard';
import { OpenAsVendorDialog } from '../../../modules/support-access/OpenAsVendorDialog';
import { SupportSessionFrame } from '../../../modules/support-access/SupportSessionFrame';
import type { SupportSession } from '../../../common/hooks/use-support-sessions-api.hook';

dayjs.extend(relativeTime);

const RANGE_KEY = 'ardealy.admin.overview.days';

function initialDays(): OverviewDays {
  try {
    const v = Number(localStorage.getItem(RANGE_KEY));
    return v === 7 || v === 90 ? v : 30;
  } catch {
    return 30;
  }
}

/**
 * The super-admin overview (dashboard v2): how the platform is doing and what
 * needs a person today — vendors and their usage, what is failing or waiting,
 * what the models cost, support and sales. One read-only call.
 */
export const AdminOverviewPage = () => {
  const { t } = useTranslation();
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportSession, setSupportSession] = useState<SupportSession | null>(null);
  const closeSupportSession = useCallback(() => setSupportSession(null), []);
  const { user } = useAuthContext();
  const api = useAdminOverviewApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [days, setDays] = useState<OverviewDays>(initialDays);
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (range: OverviewDays, fresh = false) => {
    setError(false);
    try {
      const o = await apiRef.current.getOverview(range, fresh);
      if (o?.kpis) {
        setData(o);
        useAdminNav.getState().setFromOverview(o);
      }
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    setData((d) => (d && d.days === days ? d : null));
    load(days);
  }, [days, load]);

  const changeDays = (d: OverviewDays) => {
    setDays(d);
    try {
      localStorage.setItem(RANGE_KEY, String(d));
    } catch {
      // storage blocked: the range just isn't remembered
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await load(days, true);
    setRefreshing(false);
  };

  const name = user?.firstname || (user?.email || '').split('@')[0];
  const vs = t('ADMIN_SHELL.COMMON.VS_PREVIOUS', { days });
  const k = data?.kpis;
  const trend = data?.series.map((p) => p.visualizations);

  return (
    <AdminPage
      title={t('ADMIN_OVERVIEW.TITLE')}
      header={
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-medium tracking-tight">{t('ADMIN_OVERVIEW.GREETING', { name })}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('ADMIN_OVERVIEW.SUBTITLE')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Quick action: pick a vendor and work in their account as support. */}
            <Button variant="outline" size="sm" onClick={() => setSupportOpen(true)} data-testid="overview-support-vendor">
              <UserRoundCog />
              {t('SUPPORT_ACCESS.QUICK_ACTION')}
            </Button>
            {data && (
              <span className="text-xs text-muted-foreground" title={formatDateTime(data.generatedAt)}>
                {t('ADMIN_OVERVIEW.UPDATED', { time: dayjs(data.generatedAt).fromNow() })}
              </span>
            )}
            <Button variant="ghost" size="icon-sm" onClick={refresh} disabled={refreshing} aria-label={t('ADMIN_OVERVIEW.REFRESH')}>
              <RefreshCw className={refreshing ? 'animate-spin' : undefined} />
            </Button>
            <PillTabs<OverviewDays>
              value={days}
              onChange={changeDays}
              ariaLabel={t('ADMIN_OVERVIEW.RANGE_LABEL')}
              options={[
                { value: 7, label: t('ADMIN_OVERVIEW.RANGE_7') },
                { value: 30, label: t('ADMIN_OVERVIEW.RANGE_30') },
                { value: 90, label: t('ADMIN_OVERVIEW.RANGE_90') },
              ]}
            />
          </div>
        </div>
      }
    >
      {error && !data ? (
        <EmptyState
          title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
          action={
            <Button variant="outline" onClick={() => load(days, true)}>
              {t('ADMIN_SHELL.COMMON.RETRY')}
            </Button>
          }
          className="py-24"
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="overview-kpis">
            <KpiCard
              label={t('ADMIN_OVERVIEW.KPI.ACTIVE_VENDORS')}
              value={k ? formatNumber(k.vendors.active) : ''}
              delta={k ? { current: k.vendors.active, previous: k.vendors.activePrevious } : undefined}
              hint={k ? t('ADMIN_OVERVIEW.KPI.ACTIVE_VENDORS_HINT', { active: k.vendors.active, enabled: k.vendors.enabled }) : undefined}
              icon={<Store />}
              href="/vendors"
              loading={!data}
              testId="kpi-active-vendors"
            />
            <KpiCard
              label={t('ADMIN_OVERVIEW.KPI.VISUALIZATIONS')}
              value={k ? formatNumber(k.visualizations.current) : ''}
              delta={k?.visualizations}
              hint={vs}
              icon={<Zap />}
              trend={trend}
              loading={!data}
              testId="kpi-visualizations"
            />
            <KpiCard
              label={t('ADMIN_OVERVIEW.KPI.VISITORS')}
              value={k ? formatNumber(k.visitors.current) : ''}
              delta={k?.visitors}
              hint={vs}
              icon={<Eye />}
              loading={!data}
              testId="kpi-visitors"
            />
            <KpiCard
              label={t('ADMIN_OVERVIEW.KPI.LEADS')}
              value={k ? formatNumber(k.leads.current) : ''}
              delta={k?.leads}
              hint={vs}
              icon={<Target />}
              loading={!data}
              testId="kpi-leads"
            />
            <KpiCard
              label={t('ADMIN_OVERVIEW.KPI.NEW_VENDORS')}
              value={k ? formatNumber(k.vendors.newInPeriod) : ''}
              hint={k ? t('ADMIN_OVERVIEW.KPI.NEW_VENDORS_HINT', { total: k.vendors.total, users: k.users.total }) : undefined}
              icon={<Users />}
              href="/vendors"
              loading={!data}
              testId="kpi-new-vendors"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            {data ? (
              <>
                <ActivityChart series={data.series} className="xl:col-span-2" />
                <AttentionList items={data.attention} />
              </>
            ) : (
              <>
                <Skeleton className="h-80 rounded-xl xl:col-span-2" />
                <Skeleton className="h-80 rounded-xl" />
              </>
            )}
          </div>

          {data ? <VendorLeaderboard vendors={data.vendors} /> : <Skeleton className="h-96 rounded-xl" />}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {data ? (
              <>
                <HealthCard jobs={data.health.jobs} />
                <AiSpendCard ai={data.health.ai} />
                <DeskCard data={data} />
              </>
            ) : (
              [0, 1, 2].map((i) => <Skeleton key={i} className="h-72 rounded-xl" />)
            )}
          </div>
        </>
      )}
      <OpenAsVendorDialog open={supportOpen} onOpenChange={setSupportOpen} onStarted={setSupportSession} />
      {supportSession && <SupportSessionFrame session={supportSession} onClose={closeSupportSession} />}
    </AdminPage>
  );
};

export default AdminOverviewPage;
